import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { FiArrowLeft, FiEdit3, FiPackage, FiDollarSign, FiInfo, FiTag, FiTrash2, FiCamera, FiStar } from 'react-icons/fi';
import { LoadingGrid } from '../../components/UI/Loading';
import BottomTabMenu from '../../components/UI/BottomTabMenu';
import ConfirmModal from '../../components/UI/Modal/ConfirmModal';
import { productosService } from '../../services';

const ProductoDetalle = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [producto, setProducto] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('datos');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [images, setImages] = useState([]);
  const [imagesLoading, setImagesLoading] = useState(false);

  // Opciones para selectores
  const productTypes = [
    { value: 'NATURALANDLAB', label: 'NATURALANDLAB' },
    { value: 'OTHER_BRANDS', label: 'OTRAS MARCAS' },
    { value: 'INGREDIENT', label: 'INGREDIENTE' },
    { value: 'PACKAGING_MATERIAL', label: 'MATERIAL DE EMPAQUE' }
  ];

  const categories = [
    { value: 'CAPSULES', label: 'CÁPSULAS' },
    { value: 'POWDERS', label: 'POLVOS' },
    { value: 'LIQUIDS', label: 'LÍQUIDOS' },
    { value: 'OILS', label: 'ACEITES' },
    { value: 'TEAS', label: 'INFUSIONES' },
    { value: 'CREAMS', label: 'CREMAS' },
    { value: 'LEAVES', label: 'HOJAS' }
  ];


  const statusOptions = [
    { value: 'active', label: 'ACTIVO' },
    { value: 'inactive', label: 'INACTIVO' },
    { value: 'discontinued', label: 'DESCONTINUADO' }
  ];

  useEffect(() => {
    loadProductoDetalle();
    loadImages();
  }, [id]);

  // Detectar tab actual basado en la URL
  useEffect(() => {
    const path = location.pathname;
    if (path.includes('/ventas')) {
      setActiveTab('ventas');
    } else if (path.includes('/inventario')) {
      setActiveTab('inventario');
    } else if (path.includes('/formulacion')) {
      setActiveTab('formulacion');
    } else {
      setActiveTab('datos');
    }
  }, [location]);

  const loadProductoDetalle = async () => {
    setIsLoading(true);
    try {
      const result = await productosService.getProducto(id);
      if (result.success) {
        setProducto(result.data);
      } else {
        console.error('Error al cargar producto:', result.message);
      }
    } catch (error) {
      console.error('Error cargando producto:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadImages = async () => {
    setImagesLoading(true);
    try {
      const result = await productosService.getProductImages(id);
      if (result.success) {
        setImages(result.data || []);
      }
    } catch (error) {
      console.error('Error cargando imágenes:', error);
    } finally {
      setImagesLoading(false);
    }
  };

  const getImageUrl = (image) => {
    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';
    const baseUrl = apiUrl.replace(/\/api\/v1\/?$/, '');
    if (image.medium_url) return `${baseUrl}${image.medium_url}`;
    if (image.url) return `${baseUrl}${image.url}`;
    return null;
  };

  const getLabel = (options, value) => {
    const option = options.find(opt => opt.value === value);
    return option ? option.label : value || 'N/A';
  };

  const translateCategory = (category) => {
    const categoryMap = {
      'CAPSULES': 'CÁPSULAS',
      'POWDERS': 'POLVOS',
      'LIQUIDS': 'LÍQUIDOS',
      'OILS': 'ACEITES',
      'TEAS': 'INFUSIONES',
      'CREAMS': 'CREMAS',
      'LEAVES': 'HOJAS'
    };
    return categoryMap[category] || category || 'N/A';
  };

  const translateDepartment = (department) => {
    const departmentMap = {
      'NATURALANDLAB': 'NATURALANDLAB',
      'OTHER_BRANDS': 'OTRAS MARCAS',
      'INGREDIENT': 'INGREDIENTE',
      'PACKAGING_MATERIAL': 'MATERIAL DE EMPAQUE'
    };
    return departmentMap[department] || department || 'N/A';
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      const result = await productosService.deleteProducto(id);
      if (result.success) {
        navigate('/system/productos', {
          state: {
            message: 'Producto eliminado exitosamente',
            type: 'success'
          }
        });
      } else {
        console.error('Error al eliminar producto:', result.message);
      }
    } catch (error) {
      console.error('Error eliminando producto:', error);
    } finally {
      setIsDeleting(false);
      setShowDeleteModal(false);
    }
  };

  if (isLoading) {
    return <LoadingGrid count={4} message="Cargando información del producto..." />;
  }

  if (!producto) {
    return (
      <div className="error-container">
        <h2>Producto no encontrado</h2>
        <button onClick={() => navigate('/system/productos')} className="btn btn--primary">
          Volver a Productos
        </button>
      </div>
    );
  }

  // Tabs para el menú inferior
  const productTabs = [
    {
      id: 'datos',
      label: 'Datos Producto',
      route: ''
    },
    {
      id: 'ventas',
      label: 'Ventas',
      route: '/ventas'
    },
    {
      id: 'inventario',
      label: 'Inventario',
      route: '/inventario'
    },
    ...(['BETEL', 'NATURALANDLAB', 'COTIZACIONES'].includes(producto.product_type) ? [{
      id: 'formulacion',
      label: 'Formulación',
      route: '/formulacion'
    }] : [])
  ];

  // Renderizar contenido según la tab activa
  const renderContent = () => {
    switch (activeTab) {
      case 'datos':
        return renderDatosProducto();
      case 'formulacion':
        return renderFormulacion();
      case 'ventas':
        return renderVentas();
      case 'inventario':
        return renderInventario();
      default:
        return renderDatosProducto();
    }
  };

  // Renderizar tab de Datos del Producto
  const renderDatosProducto = () => (
    <div className="cliente-detail-form">
      <div className="form-columns">
        {/* Columna Izquierda */}
        <div className="form-column">
          {/* Sección: Información Básica */}
          <div className="form-section">
            <div className="section-header">
              <FiPackage className="section-icon" />
              <h2 className="section-title">Información Básica</h2>
            </div>

            <div className="form-group">
              <label className="form-label">Nombre del Producto</label>
              <div className="form-value">{producto.name || 'N/A'}</div>
            </div>

            <div className="form-group">
              <label className="form-label">Descripción</label>
              <div className="form-value">{producto.description || 'Sin descripción'}</div>
            </div>

            <div className="form-group">
              <label className="form-label">UPC</label>
              <div className="form-value">{producto.barcode || 'N/A'}</div>
            </div>
          </div>

          {/* Sección: Proveedores */}
          <div className="form-section">
            <div className="section-header">
              <FiTag className="section-icon" />
              <h2 className="section-title">Proveedores</h2>
            </div>

            <div className="form-group">
              <label className="form-label">Proveedores</label>
              {producto.product_suppliers && producto.product_suppliers.length > 0 ? (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '4px' }}>
                  {producto.product_suppliers.map((sp) => (
                    <span key={sp.supplier_id} style={{
                      display: 'inline-flex', alignItems: 'center', gap: '4px',
                      padding: '3px 10px',
                      background: sp.is_preferred ? '#f0e6ff' : '#f0f0f0',
                      border: `1px solid ${sp.is_preferred ? '#c89de8' : '#ccc'}`,
                      borderRadius: '20px',
                      fontSize: '13px',
                      color: sp.is_preferred ? '#5a1a7a' : '#444',
                    }}>
                      {sp.is_preferred && <span style={{ fontSize: '10px', fontWeight: '700', color: '#7B2D8E' }}>★ </span>}
                      {sp.supplier_name || `Proveedor ${sp.supplier_id}`}
                    </span>
                  ))}
                </div>
              ) : (
                <div className="form-value">{producto.main_supplier_name || 'N/A'}</div>
              )}
            </div>
          </div>

          {/* Sección: Clasificación del Producto */}
          <div className="form-section">
            <div className="section-header">
              <FiInfo className="section-icon" />
              <h2 className="section-title">Clasificación del Producto</h2>
            </div>

            <div className="form-group">
              <label className="form-label">Departamento</label>
              <div className="form-value">
                {translateDepartment(producto.product_type)}
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Categoría</label>
              <div className="form-value">
                {translateCategory(producto.category)}
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Estado</label>
              <div className="form-value">
                {getLabel(statusOptions, producto.status)}
              </div>
            </div>

            {producto.product_type === 'NATURALANDLAB' && (
              <div className="form-group">
                <label className="form-label">Cliente NaturalAndLab</label>
                <div className="form-value">
                  {producto.naturalandlab_client_name || 'No asignado'}
                </div>
              </div>
            )}
          </div>

          {/* Sección: Estructura de Precios */}
          <div className="form-section">
            <div className="section-header">
              <FiDollarSign className="section-icon" />
              <h2 className="section-title">Estructura de Precios</h2>
            </div>

            {producto.category_prices && producto.category_prices.length > 0 ? (
              producto.category_prices.map((catPrice, index) => (
                <div className="form-group" key={index}>
                  <label className="form-label">{catPrice.category_name || `Categoría ${catPrice.category_code}`}</label>
                  <div className="form-value">
                    ${typeof catPrice.price === 'number' ? catPrice.price.toFixed(2) : '0.00'}
                  </div>
                </div>
              ))
            ) : (
              <div className="form-group">
                <div className="form-value">No hay precios por categoría configurados</div>
              </div>
            )}
          </div>
        </div>

        {/* Columna Derecha */}
        <div className="form-column">
          {/* Sección: Imágenes del Producto */}
          <div className="form-section">
            <div className="section-header">
              <FiCamera className="section-icon" />
              <h2 className="section-title">Imágenes del Producto</h2>
            </div>

            {imagesLoading ? (
              <div className="form-group">
                <div className="form-value" style={{ color: '#999' }}>Cargando imágenes...</div>
              </div>
            ) : images.length === 0 ? (
              <div className="form-group">
                <div className="form-value" style={{ color: '#999', fontStyle: 'italic' }}>
                  Sin imágenes cargadas
                </div>
              </div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: '10px', marginTop: '8px' }}>
                {images.map((image) => {
                  const url = getImageUrl(image);
                  return (
                    <div
                      key={image.id}
                      style={{
                        position: 'relative',
                        borderRadius: '8px',
                        overflow: 'hidden',
                        border: image.is_primary ? '2px solid #7B2D8E' : '2px solid #e0e0e0',
                        aspectRatio: '1',
                        backgroundColor: '#f5f5f5',
                      }}
                    >
                      {url && (
                        <img
                          src={url}
                          alt={image.alt_text || 'Imagen del producto'}
                          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                      )}
                      {image.is_primary && (
                        <div style={{
                          position: 'absolute',
                          top: '4px',
                          left: '4px',
                          backgroundColor: '#7B2D8E',
                          color: '#fff',
                          borderRadius: '4px',
                          padding: '2px 6px',
                          fontSize: '10px',
                          fontWeight: '600',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '3px',
                        }}>
                          <FiStar size={10} /> Principal
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Sección: Costos y Precios */}
          <div className="form-section">
            <div className="section-header">
              <FiDollarSign className="section-icon" />
              <h2 className="section-title">Costos y Precios</h2>
            </div>

            <div className="form-group">
              <label className="form-label">Precio de Costo</label>
              <div className="form-value">
                ${typeof producto.cost_price === 'number' ? producto.cost_price.toFixed(2) : '0.00'}
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Precio de Venta</label>
              <div className="form-value">
                ${typeof producto.sale_price === 'number' ? producto.sale_price.toFixed(2) : '0.00'}
              </div>
            </div>

            {producto.wholesale_price > 0 && (
              <div className="form-group">
                <label className="form-label">Precio al Por Mayor</label>
                <div className="form-value">
                  ${typeof producto.wholesale_price === 'number' ? producto.wholesale_price.toFixed(2) : '0.00'}
                </div>
              </div>
            )}
          </div>

          {/* Sección: Portal de Cliente */}
          <div className="form-section">
            <div className="section-header">
              <FiInfo className="section-icon" />
              <h2 className="section-title">Portal de Cliente</h2>
            </div>

            <div className="form-group">
              <label className="form-label">Producto Nuevo</label>
              <div className="form-value">{producto.is_new ? 'SÍ' : 'NO'}</div>
            </div>

            {producto.is_new && producto.new_until && (
              <div className="form-group">
                <label className="form-label">Nuevo hasta</label>
                <div className="form-value">
                  {new Date(producto.new_until).toLocaleDateString()}
                </div>
              </div>
            )}
          </div>

          {/* Sección: Ofertas y Promociones */}
          <div className="form-section">
            <div className="section-header">
              <FiTag className="section-icon" />
              <h2 className="section-title">Ofertas y Promociones</h2>
            </div>

            <div className="form-group">
              <label className="form-label">Producto en Oferta</label>
              <div className="form-value">{producto.is_on_sale ? 'SÍ' : 'NO'}</div>
            </div>

            {producto.is_on_sale && producto.sale_discount_price && (
              <div className="form-group">
                <label className="form-label">Precio con Descuento</label>
                <div className="form-value">
                  ${typeof producto.sale_discount_price === 'number' ? producto.sale_discount_price.toFixed(2) : '0.00'}
                </div>
              </div>
            )}

            {producto.is_on_sale && producto.sale_starts && (
              <div className="form-group">
                <label className="form-label">Oferta Inicia</label>
                <div className="form-value">
                  {new Date(producto.sale_starts).toLocaleDateString()}
                </div>
              </div>
            )}

            {producto.is_on_sale && producto.sale_ends && (
              <div className="form-group">
                <label className="form-label">Oferta Termina</label>
                <div className="form-value">
                  {new Date(producto.sale_ends).toLocaleDateString()}
                </div>
              </div>
            )}
          </div>

          {/* Sección: Control de Inventario */}
          <div className="form-section">
            <div className="section-header">
              <FiPackage className="section-icon" />
              <h2 className="section-title">Control de Inventario</h2>
            </div>

            <div className="form-group">
              <label className="form-label">Stock Actual</label>
              <div className="form-value">{producto.current_stock || 0} unidades</div>
            </div>

            <div className="form-group">
              <label className="form-label">Stock Mínimo</label>
              <div className="form-value">{producto.min_stock || 0} unidades</div>
            </div>

            {producto.max_stock && (
              <div className="form-group">
                <label className="form-label">Stock Máximo</label>
                <div className="form-value">{producto.max_stock} unidades</div>
              </div>
            )}

            <div className="form-group">
              <label className="form-label">Seguimiento de Inventario</label>
              <div className="form-value">
                {producto.track_inventory ? 'SÍ' : 'NO'}
              </div>
            </div>
          </div>

          {/* Sección: Información Adicional */}
          <div className="form-section">
            <div className="section-header">
              <FiInfo className="section-icon" />
              <h2 className="section-title">Información Adicional</h2>
            </div>

            {producto.expiration_days && (
              <div className="form-group">
                <label className="form-label">Tiempo de Mejor Uso</label>
                <div className="form-value">{producto.expiration_days} días</div>
              </div>
            )}

            <div className="form-group">
              <label className="form-label">Notas</label>
              <div className="form-value">{producto.notes || 'Sin notas'}</div>
            </div>
          </div>

          {/* Sección: Formulación - Mostrar solo indicador */}
          {producto.is_formula && (
            <div className="form-section">
              <div className="section-header">
                <FiInfo className="section-icon" />
                <h2 className="section-title">Formulación</h2>
              </div>

              <div className="form-group">
                <div className="form-value" style={{color: '#7B2D8E', fontWeight: '600'}}>
                  ✓ Este producto tiene una formulación activa
                </div>
                <small style={{color: '#666', display: 'block', marginTop: '5px'}}>
                  Ver detalles en la pestaña "Formulación"
                </small>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  // Renderizar tab de Ventas
  const renderVentas = () => (
    <div className="cliente-detail-form">
      <div className="empty-state">
        <h3>Historial de Ventas</h3>
        <p>El módulo de ventas del producto estará disponible próximamente.</p>
      </div>
    </div>
  );

  // Renderizar tab de Inventario
  const renderInventario = () => (
    <div className="cliente-detail-form">
      <div className="empty-state">
        <h3>Movimientos de Inventario</h3>
        <p>El historial de movimientos de inventario del producto estará disponible próximamente.</p>
      </div>
    </div>
  );

  // Renderizar tab de Formulación
  const renderFormulacion = () => {
    if (!producto.formula) {
      return (
        <div className="cliente-detail-form">
          <div className="empty-state">
            <h3>Sin Formulación</h3>
            <p>Este producto no tiene una formulación asociada.</p>
          </div>
        </div>
      );
    }

    const formula = producto.formula;

    return (
      <div className="cliente-detail-form">
        <div className="form-columns">
          {/* Columna Izquierda */}
          <div className="form-column">
            {/* Información General de la Fórmula */}
            <div className="form-section">
              <div className="section-header">
                <FiInfo className="section-icon" />
                <h2 className="section-title">Información General</h2>
              </div>

              <div className="form-group">
                <label className="form-label">Nombre de la Fórmula</label>
                <div className="form-value">{formula.formula_name || 'N/A'}</div>
              </div>

              <div className="form-group">
                <label className="form-label">Compañía</label>
                <div className="form-value">{formula.company_name || 'N/A'}</div>
              </div>

              <div className="form-group">
                <label className="form-label">Tipo de Fórmula</label>
                <div className="form-value">{formula.formula_type || 'N/A'}</div>
              </div>

              {formula.version && (
                <div className="form-group">
                  <label className="form-label">Versión</label>
                  <div className="form-value">{formula.version}</div>
                </div>
              )}
            </div>

            {/* Información de Empaque */}
            <div className="form-section">
              <div className="section-header">
                <FiPackage className="section-icon" />
                <h2 className="section-title">Información de Empaque</h2>
              </div>

              <div className="form-group">
                <label className="form-label">Cantidad por Envase</label>
                <div className="form-value">
                  {formula.quantity_per_container} {formula.quantity_unit || 'unidades'}
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Tipo de Cápsula</label>
                <div className="form-value">{formula.capsule_type || 'N/A'}</div>
              </div>

              {formula.mixing_time_minutes && (
                <div className="form-group">
                  <label className="form-label">Tiempo de Mezcla</label>
                  <div className="form-value">{formula.mixing_time_minutes} minutos</div>
                </div>
              )}

              {formula.best_use_date_years && (
                <div className="form-group">
                  <label className="form-label">Fecha de Mejor Uso</label>
                  <div className="form-value">{formula.best_use_date_years}</div>
                </div>
              )}

              {formula.label_printer && (
                <div className="form-group">
                  <label className="form-label">Impresión de Etiqueta</label>
                  <div className="form-value">{formula.label_printer}</div>
                </div>
              )}

              {formula.box_size && (
                <div className="form-group">
                  <label className="form-label">Medida de Caja</label>
                  <div className="form-value">{formula.box_size}</div>
                </div>
              )}

              {formula.pieces_per_box && (
                <div className="form-group">
                  <label className="form-label">Piezas por Caja</label>
                  <div className="form-value">{formula.pieces_per_box}</div>
                </div>
              )}
            </div>

            {/* Componentes Adicionales */}
            <div className="form-section">
              <div className="section-header">
                <FiTag className="section-icon" />
                <h2 className="section-title">Componentes Adicionales</h2>
              </div>

              <div className="form-group">
                <label className="form-label">Incluye Sílica</label>
                <div className="form-value">{formula.includes_silica ? 'SÍ' : 'NO'}</div>
              </div>

              {formula.includes_silica && formula.silica_cost && (
                <div className="form-group">
                  <label className="form-label">Costo de Sílica</label>
                  <div className="form-value">${parseFloat(formula.silica_cost).toFixed(4)}</div>
                </div>
              )}
            </div>
          </div>

          {/* Columna Derecha */}
          <div className="form-column">
            {/* Ingredientes */}
            <div className="form-section">
              <div className="section-header">
                <FiPackage className="section-icon" />
                <h2 className="section-title">Ingredientes ({formula.ingredients?.length || 0})</h2>
              </div>

              {formula.ingredients && formula.ingredients.length > 0 ? (
                <div style={{overflowX: 'auto'}}>
                  <table style={{width: '100%', borderCollapse: 'collapse', fontSize: '13px'}}>
                    <thead>
                      <tr style={{backgroundColor: '#f5f5f5', borderBottom: '2px solid #ddd'}}>
                        <th style={{padding: '10px', textAlign: 'left'}}>Ingrediente</th>
                        <th style={{padding: '10px', textAlign: 'right'}}>Cantidad</th>
                        <th style={{padding: '10px', textAlign: 'right'}}>Unidad</th>
                        <th style={{padding: '10px', textAlign: 'right'}}>%</th>
                      </tr>
                    </thead>
                    <tbody>
                      {formula.ingredients.map((ing, index) => (
                        <tr key={index} style={{borderBottom: '1px solid #eee'}}>
                          <td style={{padding: '10px'}}>{ing.ingredient_name}</td>
                          <td style={{padding: '10px', textAlign: 'right'}}>
                            {parseFloat(ing.quantity_per_unit).toFixed(2)}
                          </td>
                          <td style={{padding: '10px', textAlign: 'right'}}>{ing.unit}</td>
                          <td style={{padding: '10px', textAlign: 'right'}}>
                            {ing.percentage ? `${parseFloat(ing.percentage).toFixed(2)}%` : '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="form-group">
                  <div className="form-value">No hay ingredientes registrados</div>
                </div>
              )}
            </div>

            {/* Costos Estimados */}
            {(formula.total_cost_estimate || formula.cost_per_unit) && (
              <div className="form-section">
                <div className="section-header">
                  <FiDollarSign className="section-icon" />
                  <h2 className="section-title">Costos Estimados</h2>
                </div>

                {formula.total_cost_estimate && (
                  <div className="form-group">
                    <label className="form-label">Costo Total Estimado</label>
                    <div className="form-value">
                      ${parseFloat(formula.total_cost_estimate).toFixed(2)}
                    </div>
                  </div>
                )}

                {formula.cost_per_unit && (
                  <div className="form-group">
                    <label className="form-label">Costo por Unidad</label>
                    <div className="form-value">
                      ${parseFloat(formula.cost_per_unit).toFixed(6)}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Notas */}
            {formula.notes && (
              <div className="form-section">
                <div className="section-header">
                  <FiInfo className="section-icon" />
                  <h2 className="section-title">Notas</h2>
                </div>

                <div className="form-group">
                  <div className="form-value">{formula.notes}</div>
                </div>
              </div>
            )}

            {/* Estado */}
            <div className="form-section">
              <div className="section-header">
                <FiInfo className="section-icon" />
                <h2 className="section-title">Estado</h2>
              </div>

              <div className="form-group">
                <label className="form-label">Estado de la Fórmula</label>
                <div className="form-value">
                  {formula.is_active ? (
                    <span style={{color: '#28a745', fontWeight: '600'}}>✓ ACTIVA</span>
                  ) : (
                    <span style={{color: '#dc3545', fontWeight: '600'}}>✗ INACTIVA</span>
                  )}
                </div>
              </div>

              {formula.is_template && (
                <div className="form-group">
                  <label className="form-label">Plantilla</label>
                  <div className="form-value">
                    <span style={{color: '#7B2D8E', fontWeight: '600'}}>✓ Es plantilla para duplicar</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="cliente-detalle-page">
      {/* Header */}
      <div className="form-header">
        <button
          className="btn btn--ghost"
          onClick={() => navigate('/system/productos')}
          type="button"
        >
          <FiArrowLeft /> Volver a Productos
        </button>

        <div className="form-actions">
          <button
            className="btn btn--outline"
            onClick={() => setShowDeleteModal(true)}
            type="button"
            disabled={isDeleting}
          >
            <FiTrash2 /> Eliminar
          </button>
          <button
            className="btn btn--primary"
            onClick={() => navigate(`/system/productos/${id}/editar`)}
            type="button"
          >
            <FiEdit3 /> Editar Producto
          </button>
        </div>
      </div>

      {/* Contenido según tab activa */}
      {renderContent()}

      {/* Menú de navegación inferior */}
      <BottomTabMenu
        baseRoute={`/system/productos/${id}`}
        tabs={productTabs}
        currentTab={activeTab}
      />

      {/* Modal de confirmación de eliminación */}
      <ConfirmModal
        isOpen={showDeleteModal}
        onCancel={() => setShowDeleteModal(false)}
        onConfirm={handleDelete}
        title="Eliminar Producto"
        message={`¿Estás seguro de que deseas eliminar el producto "${producto?.name}"? Esta acción no se puede deshacer.`}
        confirmText="Eliminar"
        cancelText="Cancelar"
        isLoading={isDeleting}
      />
    </div>
  );
};

export default ProductoDetalle;
